import { SlidersHorizontal } from 'lucide-react'
import { useId, type ReactNode } from 'react'
import { cn } from '../../lib/cn'
import s from './FilterBar.module.css'

/**
 * Barre de filtres — le bandeau posé au-dessus d'une liste : libellé en capitales,
 * contrôles de filtre en ligne, actions rejetées à droite (« Réinitialiser »).
 *
 * Le **cadre** (fond, filet, coins, gouttières internes) appartient à ce composant ;
 * l'écran ne décide que de sa place dans la page — ses marges — via `className`. C'est la
 * règle du paquet : une *chose* stylée vient d'ici. `Demandes` la pose aujourd'hui,
 * `Contrôles`, `Archives` et `Planning` en voudront la même : elle se partage plutôt que
 * de se recopier dans quatre modules d'écran.
 *
 * `role="group"` nommé par le libellé visible : la barre s'annonce comme un ensemble aux
 * technologies d'assistance, et un test la désigne par son nom, jamais par une classe.
 */
export interface FilterBarProps {
  /** Libellé en tête de barre — c'est aussi le nom accessible du groupe. */
  label?: string
  /** Les contrôles de filtre, dans l'ordre d'affichage. */
  children?: ReactNode
  /**
   * Actions rejetées à droite — typiquement « Réinitialiser ». Une marge automatique, et
   * non un séparateur souple : l'action reste collée à droite même quand la barre passe à
   * la ligne.
   */
  actions?: ReactNode
  className?: string
}

export function FilterBar({ label = 'Filtres', children, actions, className }: FilterBarProps) {
  const labelId = useId()

  return (
    <div className={cn(s.root, className)} role="group" aria-labelledby={labelId}>
      <span id={labelId} className={s.label}>
        {/* Décoratif : le nom du groupe est déjà porté par le texte du libellé. */}
        <SlidersHorizontal size={14} aria-hidden />
        {label}
      </span>
      {children}
      {actions !== undefined && <div className={s.actions}>{actions}</div>}
    </div>
  )
}
